import React, { useState, useCallback, useMemo } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import LabelledSelect from 'shared/components/LabelledSelect';
import Form, { Field, Submit } from 'shared/containers/form';
import { gql } from 'apollo-boost';
import { useQuery, useQueryRefresh, removeConnections } from 'shared/apollo';
import { useClient } from 'shared/apollo';
import { ProgramRoot } from './styled';

export default function Program({
  programId,
  reportId,
  onClose,
  onChange,
  hideNewReportButton,
  programNames: programNames0,
}) {
  const { data: programData, refresh, isLoading } = programId
    ? useQueryRefresh(programQuery, { id: programId }, [programId]) // eslint-disable-line
    : {};
  const usersData = programNames0 ?? useQuery(usersQuery); // eslint-disable-line
  const { data: programsData, refresh: refreshPrograms } = programNames0
    ? {}
    : useQueryRefresh(allProgramsQuery); // eslint-disable-line
  const users = usersData && removeConnections(usersData.allUsers);
  const programNames =
    programNames0 ??
    (programsData &&
      removeConnections(programsData.allPrograms)?.map(({ name }) => name));
  /*useLambda('listUsers', {
    env,
    accountForUsername: user.effectiveUser.attributes.email,
  })*/
  const [fileToUpload, setFileToUpload] = useState(null); // eslint-disable-line
  const client = useClient();
  const handleSubmit = useCallback(
    async ({ name, managerId, visibility, status }) => {
      if (programNames.includes(name)) {
        alert(`A program with name ${name} already exists`);
        return false;
      }

      //const logoImage = await fileToUpload()
      let logoFileId = null;
      /*if (logoImage) {
      const resultLogo = await client.mutate({
        mutation: logoMutation,
        variables: {
          type: 'account_logo',
          logo: logoImage,
        }
      })
      console.log('result logo', resultLogo, logoImage)
      logoFileId = resultLogo.data.uploadLogo.id
    }*/
      const resultProgram = await client.mutate({
        mutation: programId ? updateProgramMutation : newProgramMutation,
        variables: {
          id: programId,
          name,
          managerId,
          visibility,
          status,
          logoFileId,
        },
      });
      if (!programId && reportId) {
        await client.mutate({
          mutation: associateMutation,
          variables: {
            id: reportId,
            programId: resultProgram.data.newProgram.id,
          },
        });
      }

      refresh && refresh();
      onChange && onChange();
      onClose && onClose();
    },
    [programNames, client, programId, reportId, refresh, onChange, onClose],
  );
  /*const [shouldCreateReport, setShouldCreateReport] = useState(false)
  const handleSaveAndCreateReportClick = useCallback(() => {
    setShouldCreateReport(true)
  }, [])*/
  const program0 = programData && programData.getProgram.edges[0].node;
  const program = useMemo(
    () =>
      programId && program0
        ? {
            ...program0,
            visibility:
              program0.visibility && program0.visibility.toLowerCase(),
            status: program0.status && program0.status.toLowerCase(),
          }
        : {
            name: '',
            logoFile: null,
            visibility: 'public',
            status: 'active',
          },
    [program0, programId],
  );

  if (programId && !programData) {
    return <LinearProgress variant="query" />;
  }

  return (
    <ProgramRoot>
      <Form initialValues={program} onSubmit={handleSubmit}>
        {isLoading && <LinearProgress variant="query" />}
        <br />
        <span className="subtitle">Program name:</span>
        <Field
          name="name"
          tooltip="The name your organization uses for this initiative. For operational programs, this may be your organization's name."
          fullWidth
          required
        />
        <br />
        {users ? (
          <>
            <span className="subtitle">Reporting Manager:</span>
            <Tooltip title="This is the primary point of contact in your organization for this reporting process.">
              <Field
                name="managerId"
                required
                component={LabelledSelect}
                options={users.map(
                  ({ userId, firstName, lastName, email }) => ({
                    label: `${firstName || ''} ${lastName || ''} (${email})`,
                    value: userId,
                  }),
                )}
                fullWidth
                label=""
              />
            </Tooltip>
          </>
        ) : (
          <LinearProgress variant="query" />
        )}
        <br />
        <br />

        <span className="subtitle">Visibility:</span>
        <Field
          name="visibility"
          component={LabelledSelect}
          options={[
            { label: 'Public', value: 'public' },
            { label: 'Donor Only', value: 'donor_only' },
          ]}
          fullWidth
          label=""
          required
        />
        <br />
        <br />
        <span className="subtitle">Status:</span>
        <Field
          name="status"
          component={LabelledSelect}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Archived', value: 'archived' },
          ]}
          fullWidth
          label=""
          required
        />
        <br />
        <br />
        <Submit variant="contained" color="primary">
          {programId ? 'Save Changes' : 'Create'}
        </Submit>
        {!programId && !hideNewReportButton && (
          <Submit variant="contained">Create Program & Report</Submit>
        )}
      </Form>
    </ProgramRoot>
  );
}

const programQuery = gql`
  query program($id: ID!) {
    getProgram(filters: { id: $id }) {
      edges {
        node {
          name
          status
          visibility
          managerId
        }
      }
    }
  }
`;

const allProgramsQuery = gql`
  query {
    allPrograms {
      edges {
        node {
          Id
          name
          managerId
        }
      }
    }
  }
`;

const newProgramMutation = gql`
  mutation program(
    $name: String!
    $visibility: String!
    $status: String!
    $managerId: Int!
    $logoFileId: Int
    $level1Note: String
  ) {
    newProgram(
      name: $name
      visibility: $visibility
      status: $status
      managerId: $managerId
      logoFileId: $logoFileId
      level1Note: $level1Note
    ) {
      success
      id
    }
  }
`;

export const updateProgramMutation = gql`
  mutation program(
    $id: Int!
    $name: String!
    $visibility: String!
    $status: String!
    $managerId: Int!
    $logoFileId: Int
    $level1Note: String
  ) {
    updateProgram(
      id: $id
      name: $name
      visibility: $visibility
      status: $status
      managerId: $managerId
      logoFileId: $logoFileId
      level1Note: $level1Note
    ) {
      success
    }
  }
`;

const associateMutation = gql`
  mutation associate($id: Int!, $programId: Int!) {
    updateReport(id: $id, programId: $programId) {
      success
    }
  }
`;

const usersQuery = gql`
  query {
    allUsers {
      edges {
        node {
          userId
          firstName
          lastName
          email
        }
      }
    }
  }
`;
